import { useEffect, useMemo, useRef, useState } from 'react';
import type { Condition, TrialPlan } from '../lib/types';
import { tone } from '../lib/audio';

interface Props {
  plan: TrialPlan;
  running: boolean;
  progress: number;
  onFlowHit: (score: number) => void;
}

function pseudo(i: number, t: number) {
  return Math.sin(i * 12.9898 + t * 0.0017) * 43758.5453 % 1;
}

function conditionCopy(condition: Condition) {
  return {
    calm: 'Watch the orbit breathe. Let counting dissolve.',
    signal: 'Detect the impossible flicker. Do not brace.',
    flow: 'Tap / click when the comet crosses the bright gate.',
    oddball: 'One pulse will break the pattern. Notice it, then let go.'
  }[condition];
}

export function StimulusField({ plan, running, progress, onFlowHit }: Props) {
  const [now, setNow] = useState(() => performance.now());
  const [score, setScore] = useState(0);
  const lastHitRef = useRef(0);
  const reduceMotion = useMemo(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false, []);

  useEffect(() => {
    if (!running) return undefined;
    let raf = 0;
    const tick = () => {
      setNow(performance.now());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running]);

  useEffect(() => {
    if (!running || reduceMotion) return undefined;
    if (plan.condition === 'signal' && progress > 0.32 && progress < 0.88) {
      const id = window.setInterval(() => tone(180 + Math.random() * 120, 0.035, 0.018), 880);
      return () => window.clearInterval(id);
    }
    if (plan.condition === 'oddball') {
      const id = window.setInterval(() => tone(progress > 0.56 && progress < 0.7 ? 820 : 410, 0.025, 0.014), 760);
      return () => window.clearInterval(id);
    }
    return undefined;
  }, [plan.condition, progress, reduceMotion, running]);

  const handlePointer = () => {
    if (!running || plan.condition !== 'flow') return;
    const phase = (progress * Math.PI * 8) % (Math.PI * 2);
    const closeness = 1 - Math.min(1, Math.abs(Math.sin(phase)) / 0.72);
    if (closeness > 0.72 && now - lastHitRef.current > 260) {
      lastHitRef.current = now;
      const next = score + Math.round(40 + closeness * 60);
      setScore(next);
      onFlowHit(next);
      tone(520 + closeness * 320, 0.045, 0.025);
    } else if (now - lastHitRef.current > 220) {
      lastHitRef.current = now;
      const next = Math.max(0, score - 15);
      setScore(next);
      onFlowHit(next);
      tone(160, 0.035, 0.012);
    }
  };

  const dots = Array.from({ length: plan.condition === 'signal' ? 56 : 36 }, (_, i) => {
    const angle = (i / 36) * Math.PI * 2 + now * 0.00012 * (i % 3 === 0 ? 1 : -1);
    const radius = 24 + ((i * 11) % 38) + Math.sin(now * 0.001 + i) * (plan.condition === 'signal' ? 8 : 3);
    const x = 50 + Math.cos(angle) * radius;
    const y = 50 + Math.sin(angle) * radius;
    const anomaly = plan.condition === 'oddball' && i === 9 && progress > 0.56 && progress < 0.72;
    const pressure = plan.condition === 'signal' ? Math.abs(pseudo(i, now)) : 0;
    return { x, y, anomaly, pressure, r: anomaly ? 3.8 : 1.2 + ((i * 7) % 18) / 10 };
  });

  const cometPhase = (progress * Math.PI * 8) % (Math.PI * 2);
  const cometX = 50 + Math.cos(cometPhase) * 29;
  const cometY = 50 + Math.sin(cometPhase * 0.8) * 20;
  const signalShake = plan.condition === 'signal' && !reduceMotion ? Math.sin(now * 0.04) * 0.8 : 0;

  return (
    <section
      className={`stimulus stimulus-${plan.condition}`}
      style={{ ['--hue' as string]: plan.hue, transform: `translate(${signalShake}px, ${-signalShake}px)` }}
      onPointerDown={handlePointer}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          handlePointer();
        }
      }}
      tabIndex={plan.condition === 'flow' ? 0 : -1}
      aria-label={`${plan.condition} stimulus field`}
    >
      <div className="stimulus-copy">
        <span>{conditionCopy(plan.condition)}</span>
        {plan.condition === 'flow' && <strong>{score} flow points</strong>}
      </div>
      <svg viewBox="0 0 100 100" className="stimulus-svg" role="img" aria-label="Animated abstract timing stimulus">
        <defs>
          <radialGradient id={`halo-${plan.id}`} cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor={`hsl(${plan.hue}, 95%, 72%)`} stopOpacity="0.48" />
            <stop offset="70%" stopColor={`hsl(${plan.hue}, 90%, 52%)`} stopOpacity="0.1" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>
        <circle cx="50" cy="50" r="48" fill={`url(#halo-${plan.id})`} />
        <circle cx="50" cy="50" r={24 + Math.sin(now * 0.001) * 2} fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="0.8" />
        <circle cx="50" cy="50" r={39 - progress * 8} fill="none" stroke="currentColor" strokeOpacity="0.18" strokeDasharray="1 4" />
        {dots.map((dot, i) => (
          <circle
            key={i}
            cx={dot.x}
            cy={dot.y}
            r={dot.r + dot.pressure * 1.5}
            fill={dot.anomaly ? '#ffef8a' : 'currentColor'}
            opacity={dot.anomaly ? 1 : 0.18 + (dot.pressure || 0) * 0.42}
          />
        ))}
        {plan.condition === 'flow' && (
          <>
            <path d="M 21 50 C 38 15, 65 85, 79 50" fill="none" stroke="#fff" strokeOpacity="0.28" strokeWidth="1.1" />
            <line x1="50" x2="50" y1="12" y2="88" stroke="#ffef8a" strokeOpacity="0.55" strokeWidth="0.8" />
            <circle cx={cometX} cy={cometY} r="3.3" fill="#ffef8a" />
            <circle cx={cometX} cy={cometY} r="7" fill="#ffef8a" opacity="0.15" />
          </>
        )}
        {plan.condition === 'signal' && (
          <path
            d={`M8 ${62 + signalShake} L24 48 L39 55 L49 24 L61 72 L75 36 L92 ${50 - signalShake}`}
            fill="none"
            stroke="#ff5c77"
            strokeWidth="1.1"
            strokeOpacity="0.8"
          />
        )}
      </svg>
      <div className="blind-progress" aria-hidden="true">
        <span style={{ transform: `scaleX(${Math.min(1, Math.max(0.03, progress))})` }} />
      </div>
    </section>
  );
}
